import React, {
  ReactElement,
  useEffect,
  useState,
  FormEvent,
  FormEventHandler,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Input, Button, message as Message } from 'antd';
import { useParams, useHistory } from 'react-router-dom';
import { FaUserAlt, FaLock } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import {
  AuthenticationRouteProps,
  AuthenticationParams,
} from './Authentication';

import { emailLogin } from '../../store/auth/actions';
import { AccountType } from '../../store/auth/types';
import { getAppGlobalState } from '../../store/app-globals/selectors';
import { getUserState } from '../../store/user/selector';
import { setError } from '../../store/app-globals/actions';

import EmailIcon from '../../assets/images/png/email-login.png';
import { logoutuser } from '../../store/user/actions';

export interface EmailLoginState {
  email: string;
  password: string;
}

const initEmailLoginState: EmailLoginState = {
  email: '',
  password: '',
};

export const EmailLogin: React.FC<AuthenticationRouteProps> = ({
  setHeaderTitle,
}): ReactElement => {
  const { t } = useTranslation();
  const { accountType } = useParams<AuthenticationParams>();
  const { isLoading, message, hasError, errorMessage } = useSelector(
    getAppGlobalState
  );
  const { user } = useSelector(getUserState);
  const [credentials, setCredentials] = useState<EmailLoginState>(
    initEmailLoginState
  );
  const _accountType: AccountType =
    accountType === 'i offer' || accountType === 'i seek'
      ? accountType
      : 'i seek';
  const dispatch = useDispatch();
  const history = useHistory();
  const handleLinkClick = (link: string) => () => {
    history.push(link);
  };

  const [valid, setValid] = useState(false);

  const handleFieldChange = (
    field: keyof EmailLoginState
  ): FormEventHandler => (e: FormEvent<HTMLInputElement>): void => {
    setCredentials({ ...credentials, [field]: e.currentTarget.value });
  };

  const validate: React.EffectCallback = (): void => {
    setValid(credentials.email !== '' && credentials.password !== '');
  };

  const onChangeAccountType: React.EffectCallback = () => {
    if (setHeaderTitle) setHeaderTitle(t('EMAIL_LOGIN'));
  };

  const handleSubmit = () => {
    dispatch(emailLogin(credentials, _accountType));
  };

  const checkAuth = () => {
    if (user.userid) {
      if (user.status === 'suspended') {
        dispatch(logoutuser());
        Message.error(
          'Oops! this account is temporarily suspended, Please contact the admin for this concern'
        );
        return;
      }
      user.authComplete
        ? user.accountType === 'admin'
          ? history.push('/admin')
          : history.push('/profile')
        : history.push('/profile/create');
    }
  };

  const error = () => {
    if (hasError) {
      Message.error(errorMessage);
      dispatch(setError(false, ''));
    }
  };

  useEffect(onChangeAccountType, [accountType]);
  useEffect(validate, [credentials]);
  useEffect(checkAuth, [isLoading]);
  useEffect(error, [hasError, errorMessage]);
  return (
    <Row className="login" justify="space-between">
      <Col
        span={24}
        flex="0 0 200px"
        className="user-icon-container padded-top"
      >
        <img src={EmailIcon} alt="email-login-icon" />
      </Col>
      <Row>
        <Col span={24} className="input-field-container">
          <Input
            size="large"
            placeholder="E-mail"
            prefix={<FaUserAlt color="rgba(255,255,255,0.7)" />}
            className="input-field"
            value={credentials.email}
            onChange={handleFieldChange('email')}
          />
        </Col>
        <Col span={24} className="input-field-container">
          <Input.Password
            size="large"
            placeholder={t('PASSWORD')}
            prefix={<FaLock color="rgba(255,255,255,0.7)" />}
            className="input-field"
            value={credentials.password}
            onChange={handleFieldChange('password')}
          />
        </Col>
        <Col flex="1 0 100px" className="bottom-link-forgot">
          <Button
            type="link"
            onClick={handleLinkClick(
              `/authentication/forgotPassword/${accountType}`
            )}
          >
            {t('FORGOT_YOUR_PASSWORD')}
          </Button>
        </Col>
      </Row>
      <Col span={24} className="login-button-container padded-bottom">
        <Button
          shape="round"
          className="login-button login-button-secondary"
          disabled={!valid}
          onClick={handleSubmit}
          loading={isLoading}
        >
          {isLoading ? t(`${message}`) : t('LOGIN')}
        </Button>
      </Col>
    </Row>
  );
};
